import axios from "axios";
import {
  getCurrentDemoLanguage,
  getDemoData,
} from "./demoData";

const localStateByLanguage = {};

const getLocalState = (language) => {
  if (!localStateByLanguage[language]) {
    const {
      demoAds,
      demoUsers,
      demoClientProjects,
      demoNotifications,
    } = getDemoData(language);

    localStateByLanguage[language] = {
      localAds: [...demoAds],
      localUsers: [...demoUsers],
      localClientProjects: [...demoClientProjects],
      localNotifications: [...demoNotifications],
    };
  }

  return localStateByLanguage[language];
};

const ok = (data = {}, status = 200) => ({ data, status, statusText: "OK", headers: {}, config: {} });

const fakeFetchResponse = (data = {}, status = 200) => ({
  ok: status >= 200 && status < 300,
  status,
  json: async () => data,
  text: async () => JSON.stringify(data),
});

const getPath = (url) => {
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname;
  } catch {
    return url;
  }
};

const getQuery = (url) => {
  try {
    const u = new URL(url, window.location.origin);
    return u.searchParams;
  } catch {
    return new URLSearchParams();
  }
};

const handleMockRequest = ({ method, url, data }) => {
  const language = getCurrentDemoLanguage();
  const {
    demoMessages,
    demoDashboardStats,
    demoProjectStatusStats,
    demoSkillDistribution,
    demoUserStats,
    demoProfessionals,
    demoProfessionalProjects,
    demoSkills,
    demoProfile,
    demoTestimonials,
    demoClientProjects,
  } = getDemoData(language);

  const {
    localAds,
    localUsers,
    localClientProjects,
    localNotifications,
  } = getLocalState(language);

  const firstClientProject = () => localClientProjects[0] || demoClientProjects[0];
  const firstProfessionalProject = () => demoProfessionalProjects[0];

  const path = getPath(url);
  const query = getQuery(url);
  const body = typeof data === "string" ? JSON.parse(data || "{}") : (data || {});

  if (path === "/login" && method === "POST") {
    return ok({ error: demoMessages.invalidCredentials }, 401);
  }

  if (path === "/register" && method === "POST") {
    const role = body.role || "client";
    return ok({
      message: role === "professional" ? demoMessages.registerProfessionalSuccess : demoMessages.registerClientSuccess,
    });
  }

  if (path === "/send-verification-email" && method === "POST") {
    return ok({ success: true });
  }

  if (path === "/ads" && method === "GET") return ok(localAds);
  if (path === "/ads" && method === "POST") {
    const newAd = {
      ad_id: Date.now(),
      title: body.title || demoMessages.demoAdTitle,
      description: body.description || demoMessages.demoAdDescription,
    };
    localStateByLanguage[language].localAds = [newAd, ...localAds];
    return ok({ adId: newAd.ad_id, ...newAd });
  }
  if (path.startsWith("/ads/") && method === "DELETE") {
    const adId = Number(path.split("/").pop());
    localStateByLanguage[language].localAds = localAds.filter((ad) => Number(ad.ad_id) !== adId);
    return ok({ success: true });
  }

  if (path === "/users" && method === "GET") return ok(localUsers);
  if (/^\/users\/\d+\/block$/.test(path) && method === "PUT") return ok({ success: true });

  if (path === "/dashboard-stats" && method === "GET") return ok(demoDashboardStats);
  if (path === "/user-stats" && method === "GET") return ok(demoUserStats);
  if (path === "/skill-distribution" && method === "GET") return ok(demoSkillDistribution);
  if (path === "/project-status-stats" && method === "GET") return ok(demoProjectStatusStats);

  if (path === "/clients-projects" && method === "GET") return ok(localClientProjects);
  if (path === "/professionals" && method === "GET") return ok(demoProfessionals);
  if (path === "/professionals-projects" && method === "GET") return ok(demoProfessionalProjects);

  if (path === "/skills" && method === "GET") return ok(demoSkills);
  if (path === "/proskills" && method === "POST") return ok({ success: true, message: demoMessages.saved });
  if (path.startsWith("/delete-proskills") && method === "DELETE") return ok({ success: true, message: demoMessages.deleted });

  if (path.startsWith("/client-projects/") && method === "GET") {
    const userId = Number(path.split("/").pop());
    const filtered = localClientProjects.filter((project) => !userId || Number(project.user_id) === userId);
    return ok(filtered);
  }

  if (path.startsWith("/delete-project/") && method === "DELETE") {
    const projectId = Number(path.split("/").pop());
    localStateByLanguage[language].localClientProjects = localClientProjects.filter((project) => Number(project.client_project_id) !== projectId);
    return ok({ message: demoMessages.projectDeletedSuccess });
  }

  if (path.startsWith("/client-project/") && method === "GET") {
    const id = Number(path.split("/").pop());
    const project = localClientProjects.find((item) => Number(item.client_project_id) === id) || firstClientProject();
    return ok(project);
  }

  if (path.startsWith("/project-professionals/") && method === "GET") {
    const projectId = Number(path.split("/").pop()) || firstClientProject().client_project_id;
    const rows = demoProfessionals.map((professional) => ({
      ...professional,
      client_project_id: projectId,
      project_skills: firstClientProject().skills,
      professional_skills: demoSkills.slice(0, 3).map((skill) => skill.skill_name).join(","),
      project_type: firstClientProject().type,
    }));
    return ok(rows);
  }

  if (path.startsWith("/professional-projects/") && method === "GET") {
    const userId = Number(path.split("/").pop());
    const rows = demoProfessionalProjects.filter((project) => !userId || Number(project.user_id) === userId);
    return ok(rows.length ? rows : demoProfessionalProjects);
  }

  if (path.startsWith("/professional-projects-details/") && method === "GET") {
    const projectId = Number(path.split("/").pop());
    const project = demoProfessionalProjects.find((item) => Number(item.professional_project_id) === projectId) || firstProfessionalProject();
    return ok(project);
  }

  if (path === "/add-client-projects" && method === "POST") {
    const created = {
      client_project_id: Date.now(),
      ...firstClientProject(),
      title: body.title || body.project_title || demoMessages.submittedProjectTitle,
      description: body.description || body.project_description || demoMessages.submittedProjectDescription,
    };
    localStateByLanguage[language].localClientProjects = [created, ...localClientProjects];
    return ok({ clientProjectId: created.client_project_id, success: true });
  }

  if (path === "/map-project" && method === "POST") return ok({ success: true });

  if (path === "/notifications" && method === "POST") {
    localStateByLanguage[language].localNotifications = [
      {
        notification_id: Date.now(),
        user_id: body.user_id || 101,
        type: body.type || "demo",
        message: body.message || demoMessages.demoNotification,
        client_project_id: body.client_project_id || firstClientProject().client_project_id,
        project_title: body.project_title || firstClientProject().title,
      },
      ...localNotifications,
    ];
    return ok({ success: true });
  }

  if (path.startsWith("/notifications/") && method === "GET") {
    const userId = Number(path.split("/").pop());
    const rows = localNotifications.filter((item) => !userId || Number(item.user_id) === userId);
    return ok(rows);
  }

  if (path.startsWith("/complete-project/") && method === "POST") return ok({ success: true });
  if (path.startsWith("/activate-profile/") && method === "POST") return ok({ success: true });

  if (path === "/professional-skills" && method === "GET") {
    return ok(demoSkills.slice(0, 3).map((skill) => ({ skill_name: skill.skill_name })));
  }

  if (path.startsWith("/profile/") && method === "GET") {
    return ok({ ...demoProfile, ...demoProfessionals[0] });
  }

  if (path === "/clients--projects" && method === "GET") {
    const status = query.get("status");
    const rows = status ? localClientProjects.filter((project) => String(project.status) === String(status)) : localClientProjects;
    return ok(rows);
  }

  if (path.startsWith("/professional-testimonials/") && method === "GET") return ok(demoTestimonials);

  if (path.startsWith("/delete-education/") && method === "DELETE") return ok({ message: demoMessages.educationDeleted });
  if (path.startsWith("/delete-work-experience") && method === "DELETE") return ok({ message: demoMessages.experienceDeleted });

  if (path === "/update-education" && method === "PUT") return ok({ success: true });
  if (path.startsWith("/education/") && method === "POST") return ok({ success: true });

  if (path === "/update-work-experience" && method === "PUT") return ok({ success: true });
  if (path.startsWith("/work-experience/") && method === "POST") return ok({ success: true });

  if (path.startsWith("/update-project/") && method === "PUT") return ok({ success: true });
  if (path.startsWith("/projects/") && method === "POST") return ok({ success: true });

  return ok({ success: true });
};

const isLocalBackend = (url) => String(url || "").includes("http://localhost:5000");

export const initDemoApi = () => {
  const originalFetch = window.fetch;

  window.fetch = async (input, init = {}) => {
    const requestUrl = typeof input === "string" ? input : input?.url;
    if (isLocalBackend(requestUrl)) {
      const method = (init.method || "GET").toUpperCase();
      const mock = handleMockRequest({ method, url: requestUrl, data: init.body });
      return fakeFetchResponse(mock.data, mock.status);
    }
    return originalFetch(input, init);
  };

  axios.interceptors.request.use(async (config) => {
    if (isLocalBackend(config.url)) {
      const method = (config.method || "GET").toUpperCase();
      const mock = handleMockRequest({ method, url: config.url, data: config.data });
      return Promise.reject({ __mockResponse: mock, config });
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.__mockResponse) {
        return Promise.resolve(error.__mockResponse);
      }
      return Promise.reject(error);
    }
  );
};
